<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<app-tab-item :tab="tab" value="pages">
					<app-heading :title="`${website.name} Pages`">
						<template v-slot:left>
							<app-button v-if="$ravello.can('website.post')"
								class="d-none d-sm-inline-flex"
								color="success"
								outlined
								small
								:to="{ name: 'website-page', params: { page: 'new' } }"
							>Add Page</app-button>
						</template>
						<template v-slot:right>
							<app-button-confirm v-if="$ravello.can('system.post')"
								class="d-none d-sm-inline-flex"
								color="warning"
								outlined
								label-confirm="Sync"
								:description="`This feature is experimental. Are you sure you want to sync template pages?`"
								small
								@click="onSyncDefaultContent"
							>Sync Template Content</app-button-confirm>
						</template>
					</app-heading>
					<app-row>
						<app-cell>
							<app-data-table
								:items="pages"
								:headers="headers"
								:total="count"
								:refresh.sync="refresh"
								sortBy="name"
								@update="fetchPages"
							>
								<template v-slot:active="{ item }">
									<app-icon v-if="item.active" icon="fa-check-circle" color="success" />
									<app-icon v-else icon="fa-times-circle" color="error" />
								</template>
								<template v-slot:actions="{ item }">
									<app-button
										v-if="$ravello.can('website.put')"
										color="secondary"
										icon="fa-paint-brush"
										table
										@click="onEdit(item._id)"
									>
										Design
									</app-button>
									<app-button
										v-if="$ravello.can('website.put')"
										color="secondary"
										icon="fa-pencil-alt"
										table
										:to="{ name: 'website-page', params: { page: item._id } }"
									>
										Edit
									</app-button>
									<app-button
										v-if="$ravello.can('website.post')"
										color="primary"
										icon="fa-save"
										table
										:disabled="!!item.page_template"
										@click="onSaveAsTemplate(item._id, item.name)"
									>
										Save As Template
									</app-button>
									<app-button-confirm
										v-if="$ravello.can('website.put')"
										color="warning"
										icon="fa-ban"
										table
										:disabled="!item.active || item.fixed"
										@click="onUnpublish(item._id)"
									>
										Unpublish
									</app-button-confirm>
									<app-button-confirm
										v-if="$ravello.can('website.delete')"
										color="error"
										icon="fa-trash"
										table
										label-confirm="Remove"
										:description="`Are you sure you want to remove page <b>${item.name}</b>?`"
										title="Remove Page"
										:disabled="item.fixed"
										@click="onDelete(item._id)"
									>
										Remove
									</app-button-confirm>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
					<app-floating-button
						class="d-inline-flex d-sm-none"
						color="success"
						icon="fa-plus"
						:to="{ name: 'website-page', params: { page: 'new' } }"
					/>
				</app-tab-item>
			</template>
		</app-tabs>
		<app-dialog v-model="edit.show" fullscreen persistent>
			<editor-web-page
				v-if="edit.show"
				:url="builderUrl"
				:pageId="edit.id"
				:websiteId="websiteId"
				@close="onCloseEditor"
				@publish="onPublished"
			/>
		</app-dialog>
		<app-dialog v-model="saveAsTemplateDialog.show" max-width="500">
			<app-form @submit="onCreateTemplate">
				<app-section>
					<div class="title">Save Page As Template</div>
					<app-row>
						<app-cell cols="12">
							<input-text
								label="Template Name"
								v-model="saveAsTemplateDialog.templateName"
								required
							/>
						</app-cell>
					</app-row>
					<app-section-actions>
						<app-button type="submit" small color="success">Create Template</app-button>
						<app-button type="cancel" small @click="saveAsTemplateDialog.show = false">Cancel</app-button>
					</app-section-actions>
				</app-section>
			</app-form>
		</app-dialog>
	</app-page>
</template>

<script>
import EditorWebPage from '@/components/editor/EditorWebPage.vue'

import { websiteContentTabs } from '@/datasources/tabs'

export default {
	components: { EditorWebPage },
	data() {
		return {
			website: {
				name: '',
			},
			pages: [],
			count: -1,
			refresh: false,
			tabs: [],
			edit: {
				show: false,
				id: null,
			},
			headers: [{
				text: 'Is published?',
				value: 'active',
				sortable: true,
			}, {
				text: 'Name',
				value: 'name',
				sortable: true,
			}, {
				text: 'Slug',
				value: 'slug',
				sortable: true,
			}, {
				text: 'URL',
				value: 'url',
				sortable: true,
			}, {
				text: 'Template',
				value: 'page_template',
				sortable: false,
				cb: item => (item.page_template && item.page_template.name ? item.page_template.name : '-'),
			}, {
				text: 'Is fixed?',
				value: 'fixed',
				sortable: true,
				cb: item => (item.fixed ? 'Yes' : 'No'),
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}],
			saveAsTemplateDialog: {
				show: false,
				pageId: null,
				templateName: '',
			},
		}
	},
	computed: {
		websiteId() {
			return this.$route.params.website
		},
		builderUrl() {
			if (this.website.domain === 'localhost') {
				return 'http://localhost:3010/builder'
			}
			return `https://${this.website.domain}/builder`
		},
	},
	async mounted() {
		this.website = await this.$ravello.graphql('website', this.websiteId)
			.fields(['name', 'domain', 'template'])
			.query()
		this.tabs = websiteContentTabs(this.websiteId)
	},
	methods: {
		async fetchPages(pagination) {
			const request = this.$ravello.graphql('pages')
				.parent(this.$ravello.graphql('website', this.websiteId))
				.fields(['_id', 'name', 'slug', 'url', 'active', 'fixed'])
				.include(this.$ravello.graphql('page_template').fields(['_id', 'name']))
				.fromPagination(pagination)

			if (this.count === -1) {
				request.count('count')
			}
			try {
				this.pages = await request.query()
				if (this.count === -1) {
					this.count = request.results.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async onUnpublish(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('pages')
					.to(id)
					.update({
						active: false,
					})
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async onSyncDefaultContent() {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('synctemplatecontent')
					.create()
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async onDelete(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('pages')
					.to(id)
					.delete()
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async onEdit(id) {
			this.edit.id = id
			this.edit.show = true
		},

		onCloseEditor() {
			this.edit.show = false
		},

		onPublished() {
			this.edit.show = false
			this.refresh = true
		},

		onSaveAsTemplate(pageId, pageName) {
			this.saveAsTemplateDialog.pageId = pageId
			this.saveAsTemplateDialog.templateName = `${pageName} Template`
			this.saveAsTemplateDialog.show = true
		},

		async onCreateTemplate() {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('pagetemplates')
					.create({
						name: this.saveAsTemplateDialog.templateName,
						template: this.website.template,
						website: this.websiteId,
						page: this.saveAsTemplateDialog.pageId,
					})
				this.saveAsTemplateDialog.show = false
				this.saveAsTemplateDialog.templateName = ''
				this.saveAsTemplateDialog.pageId = null
				this.$notifications.pushAlert('success', 'Page template created successfully')
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},
	},
}
</script>
